/**
 * AI Assessment Constants
 * Defines constants for the unified AI-powered claim assessment feature.
 * This system provides coverage analysis, decision support, and fraud detection
 * by merging capabilities from the former AI Assessment and Decision Engine services.
 *
 * Note: This is for guidance only - not final coverage decisions.
 */

/**
 * Current version of the AI assessment schema.
 * Used for tracking migrations and compatibility.
 */
export const AI_ASSESSMENT_VERSION = '2.4.0';

// ============================================================================
// Assessment Status
// ============================================================================

/**
 * Status of the AI assessment process.
 */
export const AI_ASSESSMENT_STATUSES = ['PENDING', 'PROCESSING', 'COMPLETED', 'FAILED', 'STALE'] as const;
export type AiAssessmentStatus = (typeof AI_ASSESSMENT_STATUSES)[number];

/**
 * Human-readable labels for assessment statuses
 */
export const AI_ASSESSMENT_STATUS_LABELS: Record<AiAssessmentStatus, string> = {
    PENDING: 'Not started',
    PROCESSING: 'Processing...',
    COMPLETED: 'Completed',
    FAILED: 'Failed',
    STALE: 'Needs re-run',
} as const;

// ============================================================================
// Coverage Position (replaces Coverage Level for clearer naming)
// ============================================================================

/**
 * Coverage position indicating how likely coverage applies.
 * LIKELY: Coverage appears to apply based on analysis
 * UNCLEAR: Insufficient information or ambiguous policy language
 * UNLIKELY: Coverage likely does not apply
 */
export const AI_ASSESSMENT_COVERAGE_POSITIONS = ['LIKELY', 'UNCLEAR', 'UNLIKELY'] as const;
export type AiAssessmentCoveragePosition = (typeof AI_ASSESSMENT_COVERAGE_POSITIONS)[number];

/**
 * Human-readable labels for coverage positions
 */
export const AI_ASSESSMENT_COVERAGE_POSITION_LABELS: Record<AiAssessmentCoveragePosition, string> = {
    LIKELY: 'Coverage: Likely',
    UNCLEAR: 'Coverage: Unclear',
    UNLIKELY: 'Coverage: Unlikely',
} as const;

/**
 * Colours for coverage positions (for UI display)
 */
export const AI_ASSESSMENT_COVERAGE_POSITION_COLOURS: Record<AiAssessmentCoveragePosition, string> = {
    LIKELY: 'green',
    UNCLEAR: 'gold',
    UNLIKELY: 'red',
} as const;

// ============================================================================
// Complexity Band
// ============================================================================

/**
 * Complexity bands for claim assessment complexity.
 */
export const AI_ASSESSMENT_COMPLEXITY_BANDS = ['LOW', 'MEDIUM', 'HIGH'] as const;
export type AiAssessmentComplexityBand = (typeof AI_ASSESSMENT_COMPLEXITY_BANDS)[number];

/**
 * Human-readable labels for complexity bands
 */
export const AI_ASSESSMENT_COMPLEXITY_BAND_LABELS: Record<AiAssessmentComplexityBand, string> = {
    LOW: 'Complexity: Low',
    MEDIUM: 'Complexity: Medium',
    HIGH: 'Complexity: High',
} as const;

// ============================================================================
// Exposure Band
// ============================================================================

/**
 * Exposure bands for estimated claim exposure.
 */
export const AI_ASSESSMENT_EXPOSURE_BANDS = ['MINIMAL', 'LOW', 'MEDIUM', 'HIGH', 'SEVERE'] as const;
export type AiAssessmentExposureBand = (typeof AI_ASSESSMENT_EXPOSURE_BANDS)[number];

/**
 * Human-readable labels for exposure bands
 */
export const AI_ASSESSMENT_EXPOSURE_BAND_LABELS: Record<AiAssessmentExposureBand, string> = {
    MINIMAL: 'Exposure: Minimal',
    LOW: 'Exposure: Low',
    MEDIUM: 'Exposure: Medium',
    HIGH: 'Exposure: High',
    SEVERE: 'Exposure: Severe',
} as const;

// ============================================================================
// Confidence Level
// ============================================================================

/**
 * Confidence levels for assessment conclusions.
 */
export const AI_ASSESSMENT_CONFIDENCE_LEVELS = ['HIGH', 'MEDIUM', 'LOW'] as const;
export type AiAssessmentConfidenceLevel = (typeof AI_ASSESSMENT_CONFIDENCE_LEVELS)[number];

/**
 * Human-readable labels for confidence levels
 */
export const AI_ASSESSMENT_CONFIDENCE_LEVEL_LABELS: Record<AiAssessmentConfidenceLevel, string> = {
    HIGH: 'Confidence: High',
    MEDIUM: 'Confidence: Medium',
    LOW: 'Confidence: Low',
} as const;

// ============================================================================
// Fraud Watch Level
// ============================================================================

/**
 * Fraud watch levels for risk signal monitoring.
 * LOW: No concerning patterns detected
 * ELEVATED: Patterns present that warrant attention (not accusations)
 */
export const AI_ASSESSMENT_FRAUD_LEVELS = ['LOW', 'ELEVATED'] as const;
export type AiAssessmentFraudLevel = (typeof AI_ASSESSMENT_FRAUD_LEVELS)[number];

/**
 * Human-readable labels for fraud watch levels
 */
export const AI_ASSESSMENT_FRAUD_LEVEL_LABELS: Record<AiAssessmentFraudLevel, string> = {
    LOW: 'Fraud risk: Low',
    ELEVATED: 'Fraud risk: Elevated',
} as const;

// ============================================================================
// Risk Level
// ============================================================================

/**
 * Risk levels for composite risk assessment.
 */
export const AI_ASSESSMENT_RISK_LEVELS = ['LOW', 'MEDIUM', 'HIGH', 'CRITICAL'] as const;
export type AiAssessmentRiskLevel = (typeof AI_ASSESSMENT_RISK_LEVELS)[number];

/**
 * Human-readable labels for risk levels
 */
export const AI_ASSESSMENT_RISK_LEVEL_LABELS: Record<AiAssessmentRiskLevel, string> = {
    LOW: 'Risk: Low',
    MEDIUM: 'Risk: Medium',
    HIGH: 'Risk: High',
    CRITICAL: 'Risk: Critical',
} as const;

/**
 * Risk level thresholds based on net risk score.
 * Note: Use `getRiskLevelFromScore` from `@shared/constants/decision-engine` for calculation.
 */
export const AI_ASSESSMENT_RISK_THRESHOLDS = {
    LOW: { min: 0, max: 9 },
    MEDIUM: { min: 10, max: 17 },
    HIGH: { min: 18, max: 25 },
    CRITICAL: { min: 26, max: Infinity },
} as const;

// ============================================================================
// Document Categories for Assessment
// ============================================================================

/**
 * Document categories relevant for AI assessment.
 */
export const AI_ASSESSMENT_DOCUMENT_CATEGORIES = [
    'POLICY_SCHEDULE',
    'POLICY_WORDING',
    'PDS',
    'CERTIFICATE_OF_CURRENCY',
    'POLICY_ENDORSEMENT',
    'POLICY_RENEWAL',
    'FSG',
    'EXPERT_REPORT',
    'LEGAL_ADVICE',
    'CLAIM_FORM',
    'SUPPORTING_EVIDENCE',
    'OTHER',
] as const;
export type AiAssessmentDocumentCategory = (typeof AI_ASSESSMENT_DOCUMENT_CATEGORIES)[number];

/**
 * Human-readable labels for document categories
 */
export const AI_ASSESSMENT_DOCUMENT_CATEGORY_LABELS: Record<AiAssessmentDocumentCategory, string> = {
    POLICY_SCHEDULE: 'Policy schedule',
    POLICY_WORDING: 'Policy wording',
    PDS: 'Product disclosure statement',
    CERTIFICATE_OF_CURRENCY: 'Certificate of currency',
    POLICY_ENDORSEMENT: 'Policy endorsement',
    POLICY_RENEWAL: 'Policy renewal',
    FSG: 'Financial services guide',
    EXPERT_REPORT: 'Expert report',
    LEGAL_ADVICE: 'Legal advice',
    CLAIM_FORM: 'Claim form',
    SUPPORTING_EVIDENCE: 'Supporting evidence',
    OTHER: 'Other',
} as const;

// ============================================================================
// Sub-tab Keys for UI
// ============================================================================

/**
 * Sub-tab keys for the unified AI Assessment tab.
 */
export const AI_ASSESSMENT_SUB_TABS = ['triage', 'full-assessment', 'fraud-watch', 'agent-assessment'] as const;
export type AiAssessmentSubTab = (typeof AI_ASSESSMENT_SUB_TABS)[number];

/**
 * Human-readable labels for sub-tabs
 */
export const AI_ASSESSMENT_SUB_TAB_LABELS: Record<AiAssessmentSubTab, string> = {
    triage: 'Triage',
    'full-assessment': 'Full assessment',
    'fraud-watch': 'Fraud watch',
    'agent-assessment': 'Specialist assessment',
} as const;

// ============================================================================
// Triage Card Numbers (for expandable cards in UI)
// ============================================================================

/**
 * Triage card identifiers for the numbered expandable cards.
 */
export const AI_ASSESSMENT_TRIAGE_CARDS = [
    'incident-summary',
    'urgency-assessment',
    'information-readiness',
    'early-coverage-signal',
    'fast-track-suitability',
] as const;
export type AiAssessmentTriageCard = (typeof AI_ASSESSMENT_TRIAGE_CARDS)[number];

/**
 * Human-readable labels for triage cards
 */
export const AI_ASSESSMENT_TRIAGE_CARD_LABELS: Record<AiAssessmentTriageCard, string> = {
    'incident-summary': 'Incident summary',
    'urgency-assessment': 'Urgency assessment',
    'information-readiness': 'Information readiness',
    'early-coverage-signal': 'Early coverage signal',
    'fast-track-suitability': 'Fast-track suitability',
} as const;

// ============================================================================
// Claim Fields That Trigger Assessment Re-run
// ============================================================================

/**
 * Claim fields that trigger automatic assessment re-run when changed.
 * These are FNOL-related fields that affect the analysis.
 */
export const AI_ASSESSMENT_TRIGGER_FIELDS = [
    'formData',
    'causeOfLoss',
    'claimOccurredAt',
    'claimReportedAt',
    'notes',
    'additionalInfo',
    'jsonData',
] as const;

export type AiAssessmentTriggerField = (typeof AI_ASSESSMENT_TRIGGER_FIELDS)[number];

// ============================================================================
// Feature Flag
// ============================================================================

/**
 * Feature flag key for the unified AI assessment feature.
 */
export const AI_ASSESSMENT_FEATURE_FLAG = 'enable-ai-unified-assessment';
